import type {
  SellerAssessmentJourneyStatus,
  SellerClosingJourneyStatus,
  SellerContractJourneyStatus,
  SellerProduct,
} from '__generated__/zeus/types';
import { CAKE_URL, MAPBOX_KEY } from 'components/globals';
import {
  homeResaleDetailsPath,
  isCnmlClosedResale,
  RESALE_STEPS,
  resalePhaseLabel,
  resaleStepIndex,
} from 'components/hub/homeResale/homeResale';
import type { HubSellingTransaction } from 'components/hub/useHubSellerTransactions';
import { buildPropertySpecsLine } from 'components/marketplace/property/helpers';
import { formatStreetLine } from 'helpers/formatStreetLine';

import type { TransactionCardModel } from './TransactionCard';

const SALE_PROGRESS_SEGMENTS = 6;

const buildSaleMedia = (address: HubSellingTransaction['address']) =>
  ({
    kind: 'street-view',
    street: address.street,
    city: address.city,
    state: address.state,
    mapUrl: buildMapPreviewUrl(address),
  }) as const;

interface SaleTaskSummary {
  statusLabel: string;
  footerMessage: string;
  ctaLabel: string;
}

const DEFAULT_ACTIVE_SUMMARY = {
  statusLabel: 'View progress',
  footerMessage: 'View your home sale tasks and next steps',
  ctaLabel: 'View task',
} satisfies SaleTaskSummary;

const ASSESSMENT_SUMMARIES = {
  NOT_STARTED: {
    statusLabel: 'Schedule inspection',
    footerMessage: 'Schedule an inspection with our agent to continue with the home sale',
    ctaLabel: 'View task',
  },
  SCHEDULED: {
    statusLabel: 'Assessment scheduled',
    footerMessage: 'Your home assessment is scheduled. View your home sale for details',
    ctaLabel: 'View task',
  },
  IN_PROGRESS: {
    statusLabel: 'Assessment in progress',
    footerMessage: 'Finish your home assessment to continue with the home sale',
    ctaLabel: 'View task',
  },
  READY_FOR_REVIEW: {
    statusLabel: 'Review offer',
    footerMessage: 'Your home assessment is complete. View your home sale for the next step',
    ctaLabel: 'View task',
  },
} satisfies Record<SellerAssessmentJourneyStatus, SaleTaskSummary>;

const CONTRACT_SUMMARIES = {
  SETUP_REQUIRED: {
    statusLabel: 'Complete setup',
    footerMessage: 'Finish your contract setup to continue with the home sale',
    ctaLabel: 'View task',
  },
  SIGNATURE_REQUIRED: {
    statusLabel: 'Sign contract',
    footerMessage: 'Sign your contract to continue with the home sale',
    ctaLabel: 'View task',
  },
  READY_FOR_REVIEW: {
    statusLabel: 'Review contract',
    footerMessage: 'View your contract details and next steps',
    ctaLabel: 'View task',
  },
} satisfies Record<SellerContractJourneyStatus, SaleTaskSummary>;

const CLOSING_SUMMARIES = {
  INSPECTION_SCHEDULING_REQUIRED: {
    statusLabel: 'Schedule inspection',
    footerMessage: 'Schedule an inspection with our agent to continue with the home sale',
    ctaLabel: 'View task',
  },
  INSPECTION_IN_PROGRESS: {
    statusLabel: 'Inspection in progress',
    footerMessage: 'Your inspection is underway. View your home sale for details',
    ctaLabel: 'View task',
  },
  DISCLOSURES_REQUIRED: {
    statusLabel: 'Sign disclosures',
    footerMessage: 'Complete your disclosures to keep the home sale moving',
    ctaLabel: 'View task',
  },
  HOME_INFORMATION_REQUIRED: {
    statusLabel: 'Complete home info',
    footerMessage: 'Add your home information to keep the home sale moving',
    ctaLabel: 'View task',
  },
  CHECKOUT_REQUIRED: {
    statusLabel: 'Complete checkout',
    footerMessage: 'Complete your final walkthrough before closing',
    ctaLabel: 'View task',
  },
  ADDENDUM_SIGNATURE_REQUIRED: {
    statusLabel: 'Sign addendum',
    footerMessage: 'Sign your addendum to keep the home sale moving',
    ctaLabel: 'View task',
  },
  NO_ACTION_REQUIRED: {
    statusLabel: 'View progress',
    footerMessage: 'Keep your closing on track in your home sale dashboard',
    ctaLabel: 'View task',
  },
} satisfies Record<SellerClosingJourneyStatus, SaleTaskSummary>;

export type SaleTransactionStatusFields = Pick<HubSellingTransaction, '__typename'> & {
  selectedProduct?: SellerProduct | null;
};

export type SaleTransactionHrefFields = {
  address: Pick<HubSellingTransaction['address'], 'id'>;
};

export const isActiveSaleTransaction = (transaction: SaleTransactionStatusFields): boolean => {
  if (isCnmlClosedResale(transaction)) {
    return true;
  }
  switch (transaction.__typename) {
    case 'InitializingTransaction':
    case 'PreContractTransaction':
    case 'InContractTransaction':
      return true;
    case 'ClosedTransaction':
    case 'DeniedTransaction':
    case 'WithdrawnTransaction':
      return false;
  }

  transaction.__typename satisfies never;
  return false;
};

export const isPastSaleTransaction = (transaction: SaleTransactionStatusFields): boolean => {
  if (isCnmlClosedResale(transaction)) {
    return false;
  }
  switch (transaction.__typename) {
    case 'ClosedTransaction':
    case 'DeniedTransaction':
    case 'WithdrawnTransaction':
      return true;
    case 'InitializingTransaction':
    case 'PreContractTransaction':
    case 'InContractTransaction':
      return false;
  }

  transaction.__typename satisfies never;
  return false;
};

const buildAddressLine = (transaction: HubSellingTransaction): string => {
  const { address } = transaction;
  const streetLine = formatStreetLine(address);
  const cityState = [address.city, address.state].filter(Boolean).join(', ');

  return [streetLine, cityState, address.postalCode].filter(Boolean).join(' ') || 'Your home sale';
};

/** Takes coordinates rather than a transaction so the resale page can reuse it. */
export const buildMapPreviewUrl = (coords: {
  latitude?: number | null;
  longitude?: number | null;
}): string | null => {
  const { latitude, longitude } = coords;

  if (
    latitude === null ||
    latitude === undefined ||
    longitude === null ||
    longitude === undefined ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude)
  ) {
    return null;
  }

  const lat = latitude.toFixed(5);
  const lon = longitude.toFixed(5);
  // `logo=false&attribution=false` drop Mapbox's baked-in watermark/attribution
  // from this thumbnail. Mapbox attribution is still surfaced elsewhere (the
  // interactive marketplace maps), satisfying their attribution requirement.
  return `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/pin-s+222222(${lon},${lat})/${lon},${lat},14,0/320x200@2x?access_token=${MAPBOX_KEY}&logo=false&attribution=false`;
};

export const buildSaleHref = (transaction: SaleTransactionHrefFields): string =>
  `${CAKE_URL}/dashboard/sell/${transaction.address.id}`;

const buildDetailsLine = (transaction: HubSellingTransaction): string | null => {
  const physicalDetails = transaction.application?.homeDetails?.physicalDetails;
  if (!physicalDetails) {
    return null;
  }

  const bathrooms =
    physicalDetails.bathrooms ??
    (physicalDetails.bathroomsFull !== null || physicalDetails.bathroomsHalf !== null
      ? (physicalDetails.bathroomsFull ?? 0) + (physicalDetails.bathroomsHalf ?? 0) / 2
      : null);

  return buildPropertySpecsLine(physicalDetails.bedrooms, bathrooms, physicalDetails.sqFt);
};

const summaryForStatus = <Status extends string>(
  summaries: Readonly<Record<Status, SaleTaskSummary>>,
  status: Status | null,
): SaleTaskSummary =>
  status === null ? DEFAULT_ACTIVE_SUMMARY : (summaries[status] ?? DEFAULT_ACTIVE_SUMMARY);

const buildTaskSummary = (transaction: HubSellingTransaction): SaleTaskSummary => {
  if (transaction.__typename === 'InitializingTransaction') {
    return {
      statusLabel: 'Preparing offer',
      footerMessage: 'We’re preparing your home sale details',
      ctaLabel: 'View task',
    };
  }

  if (transaction.__typename === 'PreContractTransaction') {
    const step = transaction.currentStep;
    switch (step?.__typename) {
      case 'SellerJourneyAssessmentStep':
        return summaryForStatus(ASSESSMENT_SUMMARIES, step.assessmentStatus);
      case 'SellerJourneyUnderwritingStep':
        return {
          statusLabel: 'Finalizing offer',
          footerMessage: 'We’re finalizing your offer. View your home sale for updates',
          ctaLabel: 'View task',
        };
      case 'SellerJourneyContractSigningStep':
        return summaryForStatus(CONTRACT_SUMMARIES, step.contractStatus);
      case undefined:
        return DEFAULT_ACTIVE_SUMMARY;
      default:
        step satisfies never;
        return DEFAULT_ACTIVE_SUMMARY;
    }
  }

  if (transaction.__typename === 'InContractTransaction') {
    const step = transaction.currentStep;
    switch (step?.__typename) {
      case 'SellerJourneyClosingStep':
        return summaryForStatus(CLOSING_SUMMARIES, step.closingStatus);
      case 'SellerJourneyRepricingStep':
        return summaryForStatus(CONTRACT_SUMMARIES, step.repricingStatus);
      case undefined:
        return DEFAULT_ACTIVE_SUMMARY;
      default:
        step satisfies never;
        return DEFAULT_ACTIVE_SUMMARY;
    }
  }

  return {
    statusLabel: 'View progress',
    footerMessage: 'View your home sale details',
    ctaLabel: 'View sale',
  };
};

const getSaleCompletedSegments = (transaction: HubSellingTransaction): number => {
  if (transaction.__typename === 'InitializingTransaction') {
    return 1;
  }

  if (transaction.__typename === 'PreContractTransaction') {
    switch (transaction.currentStep?.__typename) {
      case 'SellerJourneyAssessmentStep':
        return 2;
      case 'SellerJourneyUnderwritingStep':
        return 3;
      case 'SellerJourneyContractSigningStep':
        return 4;
      case undefined:
        return 1;
      default:
        transaction.currentStep satisfies never;
        return 1;
    }
  }

  if (transaction.__typename === 'InContractTransaction') {
    return 5;
  }

  return 0;
};

const getPastSaleStatusLabel = (transaction: HubSellingTransaction): string => {
  if (transaction.__typename === 'ClosedTransaction') {
    return 'Sale completed';
  }

  if (transaction.__typename === 'WithdrawnTransaction') {
    return 'Offer withdrawn';
  }

  return 'Past offer';
};

export const buildSaleTransactionCardModel = (
  transaction: HubSellingTransaction,
): TransactionCardModel | null => {
  if (!isActiveSaleTransaction(transaction)) {
    return null;
  }

  if (isCnmlClosedResale(transaction)) {
    const phase =
      transaction.__typename === 'ClosedTransaction'
        ? (transaction.homeResale?.__typename ?? null)
        : null;
    const step = resaleStepIndex(phase);

    return {
      id: transaction.id,
      href: homeResaleDetailsPath(transaction.address.id, phase ?? undefined),
      media: buildSaleMedia(transaction.address),
      addressLine: buildAddressLine(transaction),
      priceLabel: null,
      detailsLine: buildDetailsLine(transaction),
      isMuted: false,
      // The resale finishing completes this card, not the sale closing.
      isComplete: phase === 'SoldHomeResale',
      // The same four steps the page's tracker walks, so the two cannot disagree.
      totalSegments: RESALE_STEPS.length,
      completedSegments: step + 1,
      statusLabel: resalePhaseLabel(phase),
      // Once it has resold, what they are waiting on is the payment.
      footerMessage:
        phase === 'SoldHomeResale'
          ? 'Your home resold. Your second payment is on the way.'
          : 'Your home is being resold',
      ctaLabel: 'View details',
      // Distinct from a live sale's CTA so the resale entry point is countable.
      ctaAnalyticsName: 'hub-resale-view-details',
      ctaVariant: 'secondary',
    };
  }

  const taskSummary = buildTaskSummary(transaction);

  return {
    id: transaction.id,
    href: buildSaleHref(transaction),
    media: buildSaleMedia(transaction.address),
    addressLine: buildAddressLine(transaction),
    priceLabel: null,
    detailsLine: buildDetailsLine(transaction),
    isMuted: false,
    isComplete: false,
    totalSegments: SALE_PROGRESS_SEGMENTS,
    completedSegments: getSaleCompletedSegments(transaction),
    statusLabel: taskSummary.statusLabel,
    footerMessage: taskSummary.footerMessage,
    ctaLabel: taskSummary.ctaLabel,
    ctaAnalyticsName: 'hub-sale-view-task',
    ctaVariant: 'primary',
  };
};

export const buildPastSaleTransactionCardModel = (
  transaction: HubSellingTransaction,
): TransactionCardModel | null => {
  if (!isPastSaleTransaction(transaction)) {
    return null;
  }

  const completed = transaction.__typename === 'ClosedTransaction';

  return {
    id: transaction.id,
    href: buildSaleHref(transaction),
    media: buildSaleMedia(transaction.address),
    addressLine: buildAddressLine(transaction),
    priceLabel: null,
    detailsLine: buildDetailsLine(transaction),
    isMuted: true,
    isComplete: completed,
    totalSegments: SALE_PROGRESS_SEGMENTS,
    completedSegments: SALE_PROGRESS_SEGMENTS,
    statusLabel: getPastSaleStatusLabel(transaction),
    footerMessage: completed
      ? 'This home sale is complete'
      : 'This home sale offer is no longer active',
    ctaLabel: 'View details',
    ctaAnalyticsName: 'hub-sale-view-details',
    ctaVariant: 'secondary',
  };
};
